/* eslint-disable max-lines */
import { ref } from 'vue'
import { deserializeDateFromIsoString, formatDate } from '@/utils/dateUtil/dateUtils'
import type {
  EmergencyImageList,
  SelectedMarkerType
} from '@/types/ui/buildingDetails/buildingDetailsTypes'
import { MARKER_TYPE } from '@/constants/ui/common/commonUiConstants'
import {
  AI_LABEL_BUILDING_LABEL,
  EMERGENCY_INSPECTION_OPTIONS,
  EMERGENCY_OVERALL_RESULT_LABEL,
  EMERGENCY_RC_INSPECTION_OPTIONS,
  EMERGENCY_STEEL_INSPECTION_OPTIONS,
  EMERGENCY_WOOD_INSPECTION_OPTIONS
} from '@/constants/ui/buildingDetails/buildingDetailsConstants'
import { useDisasterStore } from '@/stores/disasterStore'
import { useEmergencyInspectionResultStore } from '@/stores/emergencyInspectionResultStore'
import { useAiBuildingStore } from '@/stores/aiBuildingStore'
import { EMERGENCY_S3_DIRECTORY_NAMES } from '@/constants/s3/directoryName'
import { CONSTRUCTION_TYPE } from '@/constants/api/emergencyInspectionResult/assessmentInfo/constructionType'
import type { AssessmentDetailMap } from '@/types/api/emergencyInspectionResult/assessmentDetails/assessmentDetailObject'
import { useLoadingView } from '@/features/miiaFw/composables/useLoadingView'
import MiiaFwConfig from '@/configs/config'
import { useGovernmentSettingStore } from '@/stores/governmentSettingStore'

/** 表示フラグ（true: 表示, false: 非表示） */
const isOpen = ref<boolean>(false)
/** 選択されたマーカーの種別 */
const selectedMarkerType = ref<SelectedMarkerType>(MARKER_TYPE.residentialDamage)
/** 応急危険度判定の調査日 */
const emergencyInspectionDate = ref<string | null>(null)
/** 応急危険度判定の判定結果 */
const emergencyInspectionResult = ref<string | null>(null)
/** 応急危険度判定の画像リスト（ファイル名） */
const emergencyImageList = ref<EmergencyImageList | null>(null)
/** 応急危険の判定の画像取得先ディレクトリ: {自治体コード}/{災害 ID}/{判定 ID} */
const emergencyImageDirectory = ref<string | null>(null)
/** AI倒壊判定の撮影日 */
const aiCollapseAssessmentDate = ref<string | null>(null)
/** AI倒壊判定の判定結果 */
const aiCollapseAssessmentResult = ref<string | null>(null)
/** AI倒壊判定の画像リスト（ファイル名） */
const aiCollapseImageList = ref<string[]>([])
/** AI倒壊判定の画像取得先ディレクトリ: {自治体コード}/{災害 ID}/{フライト ID}/Ai */
const aiCollapseImageDirectory = ref<string | null>(null)
/** 絞り込み部位リスト(建物種別ごと) */
const filteredEmergencyInspectionOptions = ref<{ key: string; label: string }[]>([])

export const useBuildingDetail = () => {
  const disasterStore = useDisasterStore()
  const emergencyInspectionResultStore = useEmergencyInspectionResultStore()
  const aiBuildingStore = useAiBuildingStore()
  const { setLoadingView } = useLoadingView()
  const { includesFeatureOptionsEmergency } = useGovernmentSettingStore()

  /**
   * 応急危険度判定の画像リストを作成する
   * @param なし
   * @returns 応急危険度判定の画像リスト
   */
  const buildEmergencyImageList = (): EmergencyImageList => {
    const imageList = {} as EmergencyImageList

    // デフォルト値
    const defaultObj = {} as EmergencyImageList
    EMERGENCY_INSPECTION_OPTIONS.forEach(option => {
      defaultObj[option.key] = []
    })
    const constructionType =
      emergencyInspectionResultStore.emergencyInspectionResult?.assessmentInfo.constructionType
    if (!constructionType) return defaultObj

    // 木造
    if (constructionType === CONSTRUCTION_TYPE.WOOD) {
      const assessmentDetails = emergencyInspectionResultStore.emergencyInspectionResult
        ?.assessmentDetail.assessmentDetails as AssessmentDetailMap[typeof CONSTRUCTION_TYPE.WOOD]
      if (!assessmentDetails) return defaultObj
      EMERGENCY_INSPECTION_OPTIONS.forEach(option => {
        switch (option.key) {
          case EMERGENCY_S3_DIRECTORY_NAMES.EXTERIOR:
            imageList[option.key] = assessmentDetails.step1.exterior.imageFilePathList
            break
          case EMERGENCY_S3_DIRECTORY_NAMES.SURROUNDING:
            imageList[option.key] =
              assessmentDetails.step2.neighboring.surrounding.imageFilePathList
            break
          case EMERGENCY_S3_DIRECTORY_NAMES.DIFFERENTIAL_SETTLEMENT:
            imageList[option.key] =
              assessmentDetails.step2.neighboring.differentialSettlement.imageFilePathList
            break
          case EMERGENCY_S3_DIRECTORY_NAMES.BASICS:
            imageList[option.key] = assessmentDetails.step2.neighboring.basics.imageFilePathList
            break
          case EMERGENCY_S3_DIRECTORY_NAMES.FIRST_FLOOR_SLOPE:
            imageList[option.key] =
              assessmentDetails.step2.neighboring.firstFloorSlope.imageFilePathList
            break
          case EMERGENCY_S3_DIRECTORY_NAMES.WALL:
            imageList[option.key] = assessmentDetails.step2.neighboring.wall.imageFilePathList
            break
          case EMERGENCY_S3_DIRECTORY_NAMES.CORROSION_OR_TERMITE:
            imageList[option.key] =
              assessmentDetails.step2.neighboring.corrosionOrTermite.imageFilePathList
            break
          case EMERGENCY_S3_DIRECTORY_NAMES.ROOF_TILE:
            imageList[option.key] = assessmentDetails.step2.falling.roofTile.imageFilePathList
            break
          case EMERGENCY_S3_DIRECTORY_NAMES.WINDOW_FRAME:
            imageList[option.key] = assessmentDetails.step2.falling.windowFrame.imageFilePathList
            break
          case EMERGENCY_S3_DIRECTORY_NAMES.EXTERIOR_WET_MATERIAL:
            imageList[option.key] =
              assessmentDetails.step2.falling.exteriorWetMaterial.imageFilePathList
            break
          case EMERGENCY_S3_DIRECTORY_NAMES.EXTERIOR_DRY_MATERIAL:
            imageList[option.key] =
              assessmentDetails.step2.falling.exteriorDryMaterial.imageFilePathList
            break
          case EMERGENCY_S3_DIRECTORY_NAMES.SIGNBOARD:
            imageList[option.key] = assessmentDetails.step2.falling.signboard.imageFilePathList
            break
          case EMERGENCY_S3_DIRECTORY_NAMES.OUTDOOR_STAIRS:
            imageList[option.key] = assessmentDetails.step2.falling.outdoorStairs.imageFilePathList
            break
          case EMERGENCY_S3_DIRECTORY_NAMES.OTHER_ITEM:
            imageList[option.key] = assessmentDetails.step2.falling.otherItem.imageFilePathList
            break
          default:
            imageList[option.key] = []
        }
      })
    }
    // 鉄骨造
    else if (constructionType === CONSTRUCTION_TYPE.STEEL) {
      const assessmentDetails = emergencyInspectionResultStore.emergencyInspectionResult
        ?.assessmentDetail.assessmentDetails as AssessmentDetailMap[typeof CONSTRUCTION_TYPE.STEEL]
      if (!assessmentDetails) return defaultObj
      EMERGENCY_INSPECTION_OPTIONS.forEach(option => {
        switch (option.key) {
          case EMERGENCY_S3_DIRECTORY_NAMES.EXTERIOR:
            imageList[option.key] = assessmentDetails.step1.exterior.imageFilePathList
            break
          case EMERGENCY_S3_DIRECTORY_NAMES.SURROUNDING:
            imageList[option.key] =
              assessmentDetails.step2.neighboring.surrounding.imageFilePathList
            break
          case EMERGENCY_S3_DIRECTORY_NAMES.SETTLEMENT_SLOPE_STEEL:
            imageList[option.key] =
              assessmentDetails.step2.neighboring.settlementSlopeSteel.imageFilePathList
            break
          case EMERGENCY_S3_DIRECTORY_NAMES.SLOPE:
            imageList[option.key] = assessmentDetails.step2.neighboring.slope.imageFilePathList
            break
          case EMERGENCY_S3_DIRECTORY_NAMES.BUCKLING:
            imageList[option.key] = assessmentDetails.step2.neighboring.buckling.imageFilePathList
            break
          case EMERGENCY_S3_DIRECTORY_NAMES.BRACE:
            imageList[option.key] = assessmentDetails.step2.neighboring.brace.imageFilePathList
            break
          case EMERGENCY_S3_DIRECTORY_NAMES.PILLAR_BEAM_JOINT:
            imageList[option.key] =
              assessmentDetails.step2.neighboring.pillarBeamJoint.imageFilePathList
            break
          case EMERGENCY_S3_DIRECTORY_NAMES.PILLAR_BASE:
            imageList[option.key] = assessmentDetails.step2.neighboring.pillarBase.imageFilePathList
            break
          case EMERGENCY_S3_DIRECTORY_NAMES.CORROSION:
            imageList[option.key] = assessmentDetails.step2.neighboring.corrosion.imageFilePathList
            break
          case EMERGENCY_S3_DIRECTORY_NAMES.ROOF_MATERIAL:
            imageList[option.key] = assessmentDetails.step2.falling.roofMaterial.imageFilePathList
            break
          case EMERGENCY_S3_DIRECTORY_NAMES.WINDOW_FRAME:
            imageList[option.key] = assessmentDetails.step2.falling.windowFrame.imageFilePathList
            break
          case EMERGENCY_S3_DIRECTORY_NAMES.EXTERIOR_WET_MATERIAL:
            imageList[option.key] =
              assessmentDetails.step2.falling.exteriorWetMaterial.imageFilePathList
            break
          case EMERGENCY_S3_DIRECTORY_NAMES.EXTERIOR_DRY_MATERIAL:
            imageList[option.key] =
              assessmentDetails.step2.falling.exteriorDryMaterial.imageFilePathList
            break
          case EMERGENCY_S3_DIRECTORY_NAMES.SIGNBOARD:
            imageList[option.key] = assessmentDetails.step2.falling.signboard.imageFilePathList
            break
          case EMERGENCY_S3_DIRECTORY_NAMES.OUTDOOR_STAIRS:
            imageList[option.key] = assessmentDetails.step2.falling.outdoorStairs.imageFilePathList
            break
          case EMERGENCY_S3_DIRECTORY_NAMES.OTHER_ITEM:
            imageList[option.key] = assessmentDetails.step2.falling.otherItem.imageFilePathList
            break
          default:
            imageList[option.key] = []
        }
      })
    }
    // 鉄筋及び鉄骨鉄筋コンクリート造
    else if (constructionType === CONSTRUCTION_TYPE.RC) {
      const assessmentDetails = emergencyInspectionResultStore.emergencyInspectionResult
        ?.assessmentDetail.assessmentDetails as AssessmentDetailMap[typeof CONSTRUCTION_TYPE.RC]
      if (!assessmentDetails) return defaultObj
      EMERGENCY_INSPECTION_OPTIONS.forEach(option => {
        switch (option.key) {
          case EMERGENCY_S3_DIRECTORY_NAMES.EXTERIOR:
            imageList[option.key] = assessmentDetails.step1.exterior.imageFilePathList
            break
          case EMERGENCY_S3_DIRECTORY_NAMES.PARTS_DAMAGE_GTE_3:
            imageList[option.key] =
              assessmentDetails.step2.neighboring.partsDamageGte3.imageFilePathList
            break
          case EMERGENCY_S3_DIRECTORY_NAMES.SURROUNDING:
            imageList[option.key] =
              assessmentDetails.step2.neighboring.surrounding.imageFilePathList
            break
          case EMERGENCY_S3_DIRECTORY_NAMES.GROUND_DESTRUCTION_SETTLEMENT:
            imageList[option.key] =
              assessmentDetails.step2.neighboring.groundDestructionSettlement.imageFilePathList
            break
          case EMERGENCY_S3_DIRECTORY_NAMES.SETTLEMENT_SLOPE:
            imageList[option.key] =
              assessmentDetails.step2.neighboring.settlementSlope.imageFilePathList
            break
          case EMERGENCY_S3_DIRECTORY_NAMES.PILLAR_DAMAGE_5:
            imageList[option.key] =
              assessmentDetails.step2.neighboring.pillarDamage5.imageFilePathList
            break
          case EMERGENCY_S3_DIRECTORY_NAMES.PILLAR_DAMAGE_4:
            imageList[option.key] =
              assessmentDetails.step2.neighboring.pillarDamage4.imageFilePathList
            break
          case EMERGENCY_S3_DIRECTORY_NAMES.WINDOW_FRAME:
            imageList[option.key] = assessmentDetails.step2.falling.windowFrame.imageFilePathList
            break
          case EMERGENCY_S3_DIRECTORY_NAMES.EXTERIOR_WET_MATERIAL:
            imageList[option.key] =
              assessmentDetails.step2.falling.exteriorWetMaterial.imageFilePathList
            break
          case EMERGENCY_S3_DIRECTORY_NAMES.EXTERIOR_DRY_MATERIAL:
            imageList[option.key] =
              assessmentDetails.step2.falling.exteriorDryMaterial.imageFilePathList
            break
          case EMERGENCY_S3_DIRECTORY_NAMES.SIGNBOARD:
            imageList[option.key] = assessmentDetails.step2.falling.signboard.imageFilePathList
            break
          case EMERGENCY_S3_DIRECTORY_NAMES.OUTDOOR_STAIRS:
            imageList[option.key] = assessmentDetails.step2.falling.outdoorStairs.imageFilePathList
            break
          case EMERGENCY_S3_DIRECTORY_NAMES.OTHER_ITEM:
            imageList[option.key] = assessmentDetails.step2.falling.otherItem.imageFilePathList
            break
          default:
            imageList[option.key] = []
        }
      })
    }
    return imageList
  }
  /**
   * 応急危険度判定画像対象の選択肢を構造種別ごとに返す
   * @returns 絞り込んだEMERGENCY_INSPECTION_OPTIONS
   */
  const getFilteredEmergencyInspectionOptions = (): { key: string; label: string }[] => {
    const constructionType =
      emergencyInspectionResultStore.emergencyInspectionResult?.assessmentInfo.constructionType

    let inspection_options: string[]

    if (constructionType === CONSTRUCTION_TYPE.WOOD) {
      inspection_options = EMERGENCY_WOOD_INSPECTION_OPTIONS
    } else if (constructionType === CONSTRUCTION_TYPE.STEEL) {
      inspection_options = EMERGENCY_STEEL_INSPECTION_OPTIONS
    } else if (constructionType === CONSTRUCTION_TYPE.RC) {
      inspection_options = EMERGENCY_RC_INSPECTION_OPTIONS
    } else {
      inspection_options = EMERGENCY_INSPECTION_OPTIONS.map(o => o.key)
    }

    // riskRank表記用
    const getRiskRankLabel = (rank: string | undefined): string => {
      switch (rank) {
        case '1':
          return 'A'
        case '2':
          return 'B'
        case '3':
          return 'C'
        default:
          return '-'
      }
    }

    // riskRank取得用
    const getRiskRank = (key: string): string | undefined => {
      try {
        if (constructionType === CONSTRUCTION_TYPE.WOOD) {
          const assessmentDetails = emergencyInspectionResultStore.emergencyInspectionResult
            ?.assessmentDetail
            .assessmentDetails as AssessmentDetailMap[typeof CONSTRUCTION_TYPE.WOOD]
          switch (key) {
            case EMERGENCY_S3_DIRECTORY_NAMES.SURROUNDING:
              return assessmentDetails.step2.neighboring.surrounding.riskRank
            case EMERGENCY_S3_DIRECTORY_NAMES.DIFFERENTIAL_SETTLEMENT:
              return assessmentDetails.step2.neighboring.differentialSettlement.riskRank
            case EMERGENCY_S3_DIRECTORY_NAMES.BASICS:
              return assessmentDetails.step2.neighboring.basics.riskRank
            case EMERGENCY_S3_DIRECTORY_NAMES.FIRST_FLOOR_SLOPE:
              return assessmentDetails.step2.neighboring.firstFloorSlope.riskRank
            case EMERGENCY_S3_DIRECTORY_NAMES.WALL:
              return assessmentDetails.step2.neighboring.wall.riskRank
            case EMERGENCY_S3_DIRECTORY_NAMES.CORROSION_OR_TERMITE:
              return assessmentDetails.step2.neighboring.corrosionOrTermite.riskRank
            case EMERGENCY_S3_DIRECTORY_NAMES.ROOF_TILE:
              return assessmentDetails.step2.falling.roofTile.riskRank
            case EMERGENCY_S3_DIRECTORY_NAMES.WINDOW_FRAME:
              return assessmentDetails.step2.falling.windowFrame.riskRank
            case EMERGENCY_S3_DIRECTORY_NAMES.EXTERIOR_WET_MATERIAL:
              return assessmentDetails.step2.falling.exteriorWetMaterial.riskRank
            case EMERGENCY_S3_DIRECTORY_NAMES.EXTERIOR_DRY_MATERIAL:
              return assessmentDetails.step2.falling.exteriorDryMaterial.riskRank
            case EMERGENCY_S3_DIRECTORY_NAMES.SIGNBOARD:
              return assessmentDetails.step2.falling.signboard.riskRank
            case EMERGENCY_S3_DIRECTORY_NAMES.OUTDOOR_STAIRS:
              return assessmentDetails.step2.falling.outdoorStairs.riskRank
            case EMERGENCY_S3_DIRECTORY_NAMES.OTHER_ITEM:
              return assessmentDetails.step2.falling.otherItem.riskRank
            default:
              return undefined
          }
        } else if (constructionType === CONSTRUCTION_TYPE.STEEL) {
          const assessmentDetails = emergencyInspectionResultStore.emergencyInspectionResult
            ?.assessmentDetail
            .assessmentDetails as AssessmentDetailMap[typeof CONSTRUCTION_TYPE.STEEL]
          switch (key) {
            case EMERGENCY_S3_DIRECTORY_NAMES.SURROUNDING:
              return assessmentDetails.step2.neighboring.surrounding.riskRank
            case EMERGENCY_S3_DIRECTORY_NAMES.SETTLEMENT_SLOPE_STEEL:
              return assessmentDetails.step2.neighboring.settlementSlopeSteel.riskRank
            case EMERGENCY_S3_DIRECTORY_NAMES.SLOPE:
              return assessmentDetails.step2.neighboring.slope.riskRank
            case EMERGENCY_S3_DIRECTORY_NAMES.BUCKLING:
              return assessmentDetails.step2.neighboring.buckling.riskRank
            case EMERGENCY_S3_DIRECTORY_NAMES.BRACE:
              return assessmentDetails.step2.neighboring.brace.riskRank
            case EMERGENCY_S3_DIRECTORY_NAMES.PILLAR_BEAM_JOINT:
              return assessmentDetails.step2.neighboring.pillarBeamJoint.riskRank
            case EMERGENCY_S3_DIRECTORY_NAMES.PILLAR_BASE:
              return assessmentDetails.step2.neighboring.pillarBase.riskRank
            case EMERGENCY_S3_DIRECTORY_NAMES.CORROSION:
              return assessmentDetails.step2.neighboring.corrosion.riskRank
            case EMERGENCY_S3_DIRECTORY_NAMES.ROOF_MATERIAL:
              return assessmentDetails.step2.falling.roofMaterial.riskRank
            case EMERGENCY_S3_DIRECTORY_NAMES.WINDOW_FRAME:
              return assessmentDetails.step2.falling.windowFrame.riskRank
            case EMERGENCY_S3_DIRECTORY_NAMES.EXTERIOR_WET_MATERIAL:
              return assessmentDetails.step2.falling.exteriorWetMaterial.riskRank
            case EMERGENCY_S3_DIRECTORY_NAMES.EXTERIOR_DRY_MATERIAL:
              return assessmentDetails.step2.falling.exteriorDryMaterial.riskRank
            case EMERGENCY_S3_DIRECTORY_NAMES.SIGNBOARD:
              return assessmentDetails.step2.falling.signboard.riskRank
            case EMERGENCY_S3_DIRECTORY_NAMES.OUTDOOR_STAIRS:
              return assessmentDetails.step2.falling.outdoorStairs.riskRank
            case EMERGENCY_S3_DIRECTORY_NAMES.OTHER_ITEM:
              return assessmentDetails.step2.falling.otherItem.riskRank
            default:
              return undefined
          }
        } else if (constructionType === CONSTRUCTION_TYPE.RC) {
          const assessmentDetails = emergencyInspectionResultStore.emergencyInspectionResult
            ?.assessmentDetail.assessmentDetails as AssessmentDetailMap[typeof CONSTRUCTION_TYPE.RC]
          switch (key) {
            case EMERGENCY_S3_DIRECTORY_NAMES.PARTS_DAMAGE_GTE_3:
              return assessmentDetails.step2.neighboring.partsDamageGte3.riskRank
            case EMERGENCY_S3_DIRECTORY_NAMES.SURROUNDING:
              return assessmentDetails.step2.neighboring.surrounding.riskRank
            case EMERGENCY_S3_DIRECTORY_NAMES.GROUND_DESTRUCTION_SETTLEMENT:
              return assessmentDetails.step2.neighboring.groundDestructionSettlement.riskRank
            case EMERGENCY_S3_DIRECTORY_NAMES.SETTLEMENT_SLOPE:
              return assessmentDetails.step2.neighboring.settlementSlope.riskRank
            case EMERGENCY_S3_DIRECTORY_NAMES.PILLAR_DAMAGE_5:
              return assessmentDetails.step2.neighboring.pillarDamage5.riskRank
            case EMERGENCY_S3_DIRECTORY_NAMES.PILLAR_DAMAGE_4:
              return assessmentDetails.step2.neighboring.pillarDamage4.riskRank
            case EMERGENCY_S3_DIRECTORY_NAMES.WINDOW_FRAME:
              return assessmentDetails.step2.falling.windowFrame.riskRank
            case EMERGENCY_S3_DIRECTORY_NAMES.EXTERIOR_WET_MATERIAL:
              return assessmentDetails.step2.falling.exteriorWetMaterial.riskRank
            case EMERGENCY_S3_DIRECTORY_NAMES.EXTERIOR_DRY_MATERIAL:
              return assessmentDetails.step2.falling.exteriorDryMaterial.riskRank
            case EMERGENCY_S3_DIRECTORY_NAMES.SIGNBOARD:
              return assessmentDetails.step2.falling.signboard.riskRank
            case EMERGENCY_S3_DIRECTORY_NAMES.OUTDOOR_STAIRS:
              return assessmentDetails.step2.falling.outdoorStairs.riskRank
            case EMERGENCY_S3_DIRECTORY_NAMES.OTHER_ITEM:
              return assessmentDetails.step2.falling.otherItem.riskRank
            default:
              return undefined
          }
        } else {
          // 不明構造時はriskRank無し
          return undefined
        }
      } catch {
        return undefined
      }
    }

    return EMERGENCY_INSPECTION_OPTIONS.filter(o => inspection_options.includes(o.key)).map(o => ({
      key: o.key,
      label: `${o.label}【${getRiskRankLabel(getRiskRank(o.key))}】`
    }))
  }

  /**
   * 建造物詳細コンポーネントを表示する。
   * @param emergencyAssessmentId 応急危険度判定の判定ID。空で表示する場合はヌル値を指定する。
   * @param buildingId 建物ID
   * @param selectedMarkerTypeParam 選択されたマーカーのタイプ
   * @returns なし
   */
  const open = async (
    emergencyAssessmentId: string | null | undefined,
    buildingId: string,
    selectedMarkerTypeParam: SelectedMarkerType
  ): Promise<void> => {
    try {
      setLoadingView(true)
      const fetchTasks: Promise<unknown>[] = [
        // AI倒壊判定の結果を取得
        aiBuildingStore.fetchAndSetAiBuildingResult({
          system: MiiaFwConfig.config.sysId,
          govCode: disasterStore.disaster!.govCode,
          disasterId: disasterStore.disaster!.disasterId,
          buildingId
        })
      ]
      if (emergencyAssessmentId != null && includesFeatureOptionsEmergency()) {
        // 応急危険度判定IDが指定された場合、且つ自治体設定にEmergencyAuthがある場合は応急危険度判定の結果を取得
        fetchTasks.push(
          emergencyInspectionResultStore.fetchAndSetEmergencyInspectionResult(
            disasterStore.disaster!.govCode,
            emergencyAssessmentId
          )
        )
      } else {
        // 応急危険度判定IDが指定されなかった場合はStoreをリセット
        emergencyInspectionResultStore.reset()
      }
      await Promise.all(fetchTasks)
    } finally {
      setLoadingView(false)
    }

    // 各プロパティに値を設定
    selectedMarkerType.value = selectedMarkerTypeParam
    emergencyInspectionDate.value = emergencyInspectionResultStore.emergencyInspectionResult
      ?.assessmentInfo.inspectedDatetime
      ? formatDate(
          deserializeDateFromIsoString(
            emergencyInspectionResultStore.emergencyInspectionResult.assessmentInfo
              .inspectedDatetime
          )
        )
      : null
    emergencyInspectionResult.value = emergencyInspectionResultStore.emergencyInspectionResult
      ?.emergencyAssessment.overallResult
      ? EMERGENCY_OVERALL_RESULT_LABEL[
          emergencyInspectionResultStore.emergencyInspectionResult.emergencyAssessment.overallResult
        ].label
      : null
    emergencyImageList.value = buildEmergencyImageList()
    filteredEmergencyInspectionOptions.value = getFilteredEmergencyInspectionOptions()
    emergencyImageDirectory.value = `${disasterStore.disaster?.govCode}/${disasterStore.disaster?.disasterId}/${emergencyInspectionResultStore.emergencyInspectionResult?.assessmentId}`
    if (aiBuildingStore.aiBuildingResult == null) {
      // 取得できなかった場合はnull
      aiCollapseAssessmentDate.value = null
    } else {
      // 日付テキストに変換
      const minText = formatDate(aiBuildingStore.aiBuildingResult.imageInsertDateMin)
      const maxText = formatDate(aiBuildingStore.aiBuildingResult.imageInsertDateMax)
      if (minText === maxText) {
        // 家屋切り出し画像の登録日の最小・最大が同じ場合、撮影日は片方のみ表示
        aiCollapseAssessmentDate.value = minText
      } else {
        // 家屋切り出し画像の登録日の最小・最大が異なる場合、撮影日はハイフン繋ぎで表示
        aiCollapseAssessmentDate.value = `${minText} - ${maxText}`
      }
    }

    aiCollapseAssessmentResult.value = aiBuildingStore.aiBuildingResult
      ? (AI_LABEL_BUILDING_LABEL?.[aiBuildingStore.aiBuildingResult.aiLabelBuilding].label ?? null)
      : null
    aiCollapseImageList.value =
      aiBuildingStore.aiBuildingResult?.images.map(
        image => `${image.flightId}/Ai/${image.fileName}`
      ) ?? []
    aiCollapseImageDirectory.value = `${disasterStore.disaster?.govCode}/${disasterStore.disaster?.disasterId}`

    // コンポーネントを表示
    isOpen.value = true
  }

  /**
   * 閉じる
   * @param なし
   * @return なし
   */
  const close = (): void => {
    isOpen.value = false
  }

  /**
   * 変数群のリセット
   * @param なし
   * @returns なし
   */
  const reset = () => {
    isOpen.value = false
    selectedMarkerType.value = MARKER_TYPE.residentialDamage
    emergencyInspectionDate.value = null
    emergencyInspectionResult.value = null
    emergencyImageList.value = null
    emergencyImageDirectory.value = null
    aiCollapseAssessmentDate.value = null
    aiCollapseAssessmentResult.value = null
    aiCollapseImageList.value = []
    aiCollapseImageDirectory.value = null
    filteredEmergencyInspectionOptions.value = []
  }

  return {
    isOpen,
    selectedMarkerType,
    emergencyInspectionDate,
    emergencyInspectionResult,
    emergencyImageList,
    emergencyImageDirectory,
    aiCollapseAssessmentDate,
    aiCollapseAssessmentResult,
    aiCollapseImageList,
    aiCollapseImageDirectory,
    filteredEmergencyInspectionOptions,
    open,
    close,
    reset
  }
}
